/**
 * fs-flavors.js — Flavor grid (sections/fs-flavors.liquid)
 *
 * Extracted from the section's inline <script> so it is cached across pages.
 * Loaded with `async` from a tag that follows the section markup, so by the
 * time it executes the section's DOM already exists: it initialises
 * immediately rather than waiting for DOMContentLoaded, which on a slow
 * connection can be 10+ seconds after the cards are on screen. Runs once per
 * section instance, and again when the theme editor reloads the section.
 */
(function() {
  'use strict';

  function initFlavors() {
    const section = document.querySelector('.fs-flavors--v3');
    if (!section || section.hasAttribute('data-fs-init')) return;
    section.setAttribute('data-fs-init', '');

    initDeferredImages(section);


      // Strength pill selection - Updates the variant ID on the add to cart button
      const productCards = section.querySelectorAll('.fs-flavors__product-card--v3');

      productCards.forEach(card => {
        const strengthSelector = card.querySelector('[data-strength-selector]');
        const addToCartBtn = card.querySelector('[data-add-to-cart]');

        if (!strengthSelector || !addToCartBtn) return;

        const pills = strengthSelector.querySelectorAll('.fs-flavors__strength-pill--v3');

        pills.forEach(pill => {
          pill.addEventListener('click', () => {
            // Skip if disabled/unavailable
            if (pill.disabled || pill.classList.contains('is-unavailable')) return;

            // Update selected state
            pills.forEach(p => p.classList.remove('is-selected'));
            pill.classList.add('is-selected');

            // Update the variant ID on the add to cart button
            const variantId = pill.dataset.variantId;
            if (variantId) {
              addToCartBtn.dataset.variantId = variantId;

              // Check if variant is available
              const isAvailable = pill.dataset.available === 'true';
              addToCartBtn.disabled = !isAvailable;

              const btnText = addToCartBtn.querySelector('span');
              if (btnText) {
                if (!isAvailable) {
                  btnText.textContent = 'Sold Out';
                } else if (addToCartBtn.dataset.purchaseType === 'subscribe') {
                  btnText.textContent = 'Subscribe \u00B7 Add';
                } else {
                  btnText.textContent = 'Add Roll to Cart';
                }
              }
            }

            // Update product image if variant has a different image
            const variantImage = pill.dataset.variantImage;
            if (variantImage) {
              const imageContainer = card.querySelector('[data-image-carousel]');
              if (imageContainer) {
                const firstSlide = imageContainer.querySelector('[data-slide="0"]');
                if (firstSlide) {
                  const img = firstSlide.querySelector('img');
                  if (img) {
                    // Update image src and srcset
                    img.src = variantImage;
                    if (pill.dataset.variantImageSrcset) {
                      img.srcset = pill.dataset.variantImageSrcset;
                    }
                    if (pill.dataset.variantImageAlt) {
                      img.alt = pill.dataset.variantImageAlt;
                    }
                    // Add a subtle transition effect
                    img.style.opacity = '0';
                    setTimeout(() => {
                      img.style.opacity = '1';
                    }, 50);
                  }
                }
              }
            }
          });
        });

        // Keyboard navigation for strength pills
        strengthSelector.addEventListener('keydown', (e) => {
          const pillsArray = Array.from(pills);
          const currentIndex = pillsArray.findIndex(p => p === document.activeElement);
          if (currentIndex === -1) return;

          let newIndex;

          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            newIndex = (currentIndex + 1) % pillsArray.length;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            newIndex = (currentIndex - 1 + pillsArray.length) % pillsArray.length;
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pillsArray[currentIndex].click();
            return;
          }

          if (newIndex !== undefined) {
            pillsArray[newIndex].focus();
          }
        });
      });

      // Subscribe / One-Time toggle
      const subToggles = section.querySelectorAll('[data-sub-toggle]');
      subToggles.forEach(toggle => {
        const options = toggle.querySelectorAll('[data-sub-option]');
        options.forEach(opt => {
          opt.addEventListener('click', () => {
            options.forEach(o => o.classList.remove('is-selected'));
            opt.classList.add('is-selected');
            opt.querySelector('input').checked = true;

            const card = toggle.closest('[data-product-card]');
            const btn = card.querySelector('[data-add-to-cart]');
            const btnText = btn?.querySelector('span');
            const isSubscribe = opt.dataset.subOption === 'subscribe';

            applyPriceState(card, toggle, isSubscribe);

            if (isSubscribe) {
              if (btnText) btnText.textContent = 'Subscribe \u00B7 Add';
              btn.dataset.purchaseType = 'subscribe';
            } else {
              if (btnText) btnText.textContent = 'Add Roll to Cart';
              btn.dataset.purchaseType = 'onetime';
            }
          });
        });

        // Initialize from the server-rendered default option (Subscribe when a
        // subscription plan exists, otherwise One-Time).
        const card = toggle.closest('[data-product-card]');
        const btn = card?.querySelector('[data-add-to-cart]');
        if (btn) {
          const selectedOpt = toggle.querySelector('[data-sub-option].is-selected')
            || toggle.querySelector('[data-sub-option]');
          const isSubscribe = selectedOpt && selectedOpt.dataset.subOption === 'subscribe';
          btn.dataset.purchaseType = isSubscribe ? 'subscribe' : 'onetime';
          const btnText = btn.querySelector('span');
          if (btnText) {
            btnText.textContent = isSubscribe ? 'Subscribe · Add' : 'Add Roll to Cart';
          }
          applyPriceState(card, toggle, isSubscribe);
        }
      });

      /*
       * Split-Tablet price sync.
       * Updates both Roll + Can cells, toggles strike+save chip,
       * and re-computes the savings %. Roll price comes from the
       * toggle's data-can-price / data-can-sub-price; Can price is
       * roll ÷ cansPerRoll (5).
       */
      function applyPriceState(card, toggle, isSubscribe) {
        if (!card || !toggle) return;
        const cansPerRoll = 5;
        const basePrice = parseInt(toggle.dataset.canPrice, 10);
        const subPrice = parseInt(toggle.dataset.canSubPrice, 10);
        const onetimePrice = parseInt(toggle.dataset.canOnetimePrice, 10);
        if (isNaN(basePrice)) return;

        const rollEl = card.querySelector('[data-price-current]');
        const canEl  = card.querySelector('[data-price-can]');

        const fmt = (cents) => '$' + (cents / 100).toFixed(2);

        // Subscribe is 25% off MSRP; one-time is full price. Only strike the MSRP
        // when the selected mode's price is actually below the base roll price.
        const rollPrice = isSubscribe
          ? (isNaN(subPrice) ? basePrice : subPrice)
          : (isNaN(onetimePrice) ? basePrice : onetimePrice);
        const isDiscounted = rollPrice < basePrice;

        if (rollEl) {
          rollEl.textContent = fmt(rollPrice);
          rollEl.classList.toggle('fs-flavors__price-current--discounted', isDiscounted);
        }
        if (canEl) {
          canEl.textContent = fmt(rollPrice / cansPerRoll);
          canEl.classList.toggle('fs-flavors__price-current--discounted', isDiscounted);
        }
        // Show the struck MSRP ("was") only in a discounted mode (Subscribe & Save).
        card.querySelectorAll('.fs-flavors__price-was--v3').forEach(function (el) {
          el.style.display = isDiscounted ? '' : 'none';
        });
      }

      // Add to Cart functionality - Uses actual Shopify AJAX API
      const addButtons = section.querySelectorAll('[data-add-to-cart]');

      addButtons.forEach(button => {
        button.addEventListener('click', async () => {
          // Don't proceed if disabled
          if (button.disabled) return;

          const variantId = button.dataset.variantId;
          const productHandle = button.dataset.productHandle;

          // Must have a variant ID
          if (!variantId) {
            console.error('No variant ID found');
            return;
          }

          const btnText = button.querySelector('span');
          const originalText = btnText?.textContent || 'Add Roll to Cart';

          // Show loading state
          button.classList.add('is-loading');
          button.disabled = true;

          try {
            // Shopify AJAX API call
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify((() => {
                const payload = {
                  id: parseInt(variantId, 10),
                  quantity: 1
                };
                // Attach selling plan only for subscribe (defaults to monthly)
                if (button.dataset.purchaseType === 'subscribe') {
                  const sellingPlanId = button.dataset.sellingPlanId;
                  if (sellingPlanId) {
                    payload.selling_plan = parseInt(sellingPlanId, 10);
                  }
                }
                return payload;
              })())
            });

            if (!response.ok) {
              throw new Error('Failed to add to cart');
            }

            const data = await response.json();

            // Show success state
            button.classList.remove('is-loading');
            button.classList.add('is-success');
            if (btnText) btnText.textContent = 'Added!';

            // Update cart count in header
            const cartCountEls = document.querySelectorAll('[data-cart-count]');
            if (cartCountEls.length) {
              const cartRes = await fetch('/cart.js');
              const cartData = await cartRes.json();
              cartCountEls.forEach(el => el.textContent = cartData.item_count);
            }

            // Dispatch custom event for cart drawer (triggers refresh and open)
            document.dispatchEvent(new CustomEvent('cart:add', {
              detail: { item: data, handle: productHandle }
            }));

            // Reset after delay
            setTimeout(() => {
              button.classList.remove('is-success');
              if (btnText) btnText.textContent = originalText;
              button.disabled = false;
            }, 2000);

          } catch (error) {
            console.error('Failed to add to cart:', error);
            button.classList.remove('is-loading');
            if (btnText) btnText.textContent = 'Error - Try Again';
            button.disabled = false;

            setTimeout(() => {
              if (btnText) btnText.textContent = originalText;
            }, 2000);
          }
        });
      });

      // Mobile Carousel Sync
      const grid = section.querySelector('[data-flavors-grid]');
      const mobileDots = section.querySelectorAll('[data-mobile-dot]');
      const cards = section.querySelectorAll('.fs-flavors__product-card--v3');

      if (grid && mobileDots.length > 0 && window.innerWidth < 640) {
        let scrollTimeout;

        grid.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);

          scrollTimeout = setTimeout(() => {
            const scrollLeft = grid.scrollLeft;
            const cardWidth = cards[0]?.offsetWidth || 0;
            const gap = parseFloat(getComputedStyle(grid).gap) || 16;
            const currentIndex = Math.round(scrollLeft / (cardWidth + gap));

            mobileDots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === currentIndex);
            });
          }, 50);
        });

        // Dot click to scroll
        mobileDots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            const card = cards[index];
            if (card) {
              const scrollLeft = card.offsetLeft - (grid.offsetWidth - card.offsetWidth) / 2;
              grid.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            }
          });
        });
      }
  }

  // ===========================================
  // IMAGES
  // - Primary slide: blur-up placeholder hidden once the real image decodes.
  // - Hover slides (data-src): no request until the card is hovered/focused.
  //   Mouse devices fetch them at idle after load so the first hover is
  //   instant; touch devices (no hover) never download them.
  // ===========================================
  function hydrate(img) {
    if (!img.dataset.src) return;
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
    }
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-deferred');
  }

  function initDeferredImages(section) {
    section.querySelectorAll('.fs-flavors__product-card--v3').forEach(card => {
      card.querySelectorAll('.fs-flavors__image-slide--v3[data-slide="0"]').forEach(slide => {
        const img = slide.querySelector('img');
        if (!img) return;
        const done = () => slide.classList.add('is-loaded');
        if (img.complete && img.naturalWidth > 0) done();
        else img.addEventListener('load', done, { once: true });
      });

      const wake = () => card.querySelectorAll('img[data-src]').forEach(hydrate);
      card.addEventListener('pointerenter', wake, { once: true });
      card.addEventListener('focusin', wake, { once: true });
    });

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const all = () => section.querySelectorAll('img[data-src]').forEach(hydrate);
      const schedule = () => {
        if ('requestIdleCallback' in window) requestIdleCallback(all, { timeout: 4000 });
        else setTimeout(all, 2000);
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true });
    }
  }

  initFlavors();
  // Safety net: if this ever runs before the section markup (e.g. moved into
  // the head), pick the section up at DOMContentLoaded instead.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFlavors);
  }
  document.addEventListener('shopify:section:load', initFlavors);
})();
